import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Trash } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

function Shell({ title, info, onBack, children }) {
  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-fuchsia-600 text-white" style={info?.brand_color ? { background: info.brand_color } : {}}>
        <div className="max-w-lg mx-auto px-3 py-3 flex items-center gap-3">
          <button onClick={onBack} className="h-9 w-9 border border-white/30 flex items-center justify-center hover:bg-white/10">
            <ArrowLeft weight="bold" size={16}/>
          </button>
          <div className="font-display font-black text-lg">{title}</div>
        </div>
      </header>
      <div className="max-w-lg mx-auto p-3">{children}</div>
    </div>
  );
}

export function PayRentScreen({ token, info, onBack }) {
  const [lease, setLease] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${BASE}/api/public/report/${token}/lease`)
      .then(r => setLease(r.data))
      .catch(() => setLease({ has_lease: false }));
  }, [token]);

  const pay = async () => {
    if (!name.trim()) { toast.error("Enter your name"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/api/public/report/${token}/pay-rent`, {
        tenant_name: name, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't start checkout"));
    } finally { setBusy(false); }
  };

  return (
    <Shell title="💵 Pay Rent" info={info} onBack={onBack}>
      {!lease ? (
        <div className="p-6 text-center font-black uppercase text-slate-400 text-xs">Loading…</div>
      ) : !lease.has_lease ? (
        <div className="p-4 bg-white border-2 border-amber-500" data-testid="pay-rent-no-lease">
          <div className="font-black text-lg">No rent set up yet</div>
          <div className="text-sm text-slate-700 mt-1">Your property manager hasn't configured rent for this unit yet. Contact <b>{info?.crew_name}</b> to set it up.</div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="p-4 bg-white border-2 border-[#0f172a]">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Monthly rent</div>
            <div className="font-display font-black text-4xl mt-1">${(lease.monthly_rent_cents/100).toFixed(2)}</div>
            <div className="text-xs text-slate-600 mt-1">Due day: {lease.due_day} of each month{lease.unit ? ` · Unit ${lease.unit}` : ""}</div>
          </div>
          <div className="p-4 bg-white border-2 border-[#0f172a] space-y-2">
            <label className="block">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Your Name</div>
              <input value={name} onChange={e => setName(e.target.value)} data-testid="pay-rent-name"
                className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            </label>
            <button onClick={pay} disabled={busy || !name.trim()} data-testid="pay-rent-submit"
              className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
              {busy ? "Redirecting…" : `Pay $${(lease.monthly_rent_cents/100).toFixed(2)} — Secure Stripe Checkout`}
            </button>
          </div>
        </div>
      )}
    </Shell>
  );
}

export function PetsScreen({ token, info, onBack }) {
  const [pets, setPets] = useState([]);
  const [form, setForm] = useState({ name: "", species: "dog", breed: "", weight_lbs: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${BASE}/api/public/report/${token}/pets`).then(r => setPets(r.data.pets || []));
  useEffect(() => { load(); }, [token]);

  const add = async () => {
    if (!form.name.trim()) { toast.error("Pet name required"); return; }
    setBusy(true);
    try {
      await axios.post(`${BASE}/api/public/report/${token}/pets`, {
        ...form, weight_lbs: form.weight_lbs ? parseInt(form.weight_lbs) : null,
      });
      toast.success("Pet added");
      setForm({ name: "", species: "dog", breed: "", weight_lbs: "", notes: "" });
      load();
    } catch (e) { toast.error("Couldn't add"); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this pet from your file?")) return;
    try { await axios.delete(`${BASE}/api/public/report/${token}/pets/${id}`); load(); }
    catch (e) { toast.error("Couldn't remove"); }
  };

  return (
    <Shell title="🐾 Pets on File" info={info} onBack={onBack}>
      <div className="space-y-2 mb-4" data-testid="pets-list">
        {pets.map(p => (
          <div key={p.id} className="p-3 bg-white border-2 border-[#0f172a] flex items-center gap-3" data-testid={`pet-${p.id}`}>
            <div className="text-2xl">{p.species === "dog" ? "🐕" : p.species === "cat" ? "🐈" : "🐾"}</div>
            <div className="flex-1">
              <div className="font-black">{p.name} <span className="text-slate-400 font-normal">· {p.species}</span></div>
              <div className="text-xs text-slate-600">{[p.breed, p.weight_lbs && `${p.weight_lbs} lbs`].filter(Boolean).join(" · ")}</div>
              {p.notes && <div className="text-xs text-slate-500 italic">{p.notes}</div>}
            </div>
            <button onClick={() => remove(p.id)} data-testid={`pet-remove-${p.id}`} className="p-2 text-red-600"><Trash weight="bold" size={16}/></button>
          </div>
        ))}
        {pets.length === 0 && <div className="p-4 text-center text-sm text-slate-500 italic">No pets on file for this unit yet.</div>}
      </div>
      <div className="p-3 bg-white border-2 border-fuchsia-600 space-y-2">
        <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-600">Add Pet</div>
        <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Pet name" data-testid="pet-name" className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.species} onChange={e => setForm({...form, species: e.target.value})} data-testid="pet-species" className="h-11 px-2 border-2 border-slate-300 text-sm bg-white">
            <option value="dog">Dog</option><option value="cat">Cat</option><option value="bird">Bird</option><option value="fish">Fish</option><option value="reptile">Reptile</option><option value="other">Other</option>
          </select>
          <input value={form.breed} onChange={e => setForm({...form, breed: e.target.value})} placeholder="Breed (opt)" data-testid="pet-breed" className="h-11 px-3 border-2 border-slate-300 text-sm"/>
          <input value={form.weight_lbs} type="number" onChange={e => setForm({...form, weight_lbs: e.target.value})} placeholder="Weight lbs" data-testid="pet-weight" className="h-11 px-3 border-2 border-slate-300 text-sm"/>
          <input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Notes (opt)" data-testid="pet-notes" className="h-11 px-3 border-2 border-slate-300 text-sm"/>
        </div>
        <button onClick={add} disabled={busy} data-testid="pet-add" className="w-full h-11 bg-fuchsia-600 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
          {busy ? "Adding…" : "Add Pet"}
        </button>
      </div>
    </Shell>
  );
}

export function NoticeScreen({ token, info, onBack }) {
  const [form, setForm] = useState({ tenant_name: "", tenant_phone: "", notice_days: 60, reason: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async () => {
    if (!form.tenant_name.trim()) { toast.error("Enter your name"); return; }
    if (!window.confirm(`Submit ${form.notice_days}-day notice to vacate? This will notify ${info?.crew_name} immediately.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${BASE}/api/public/report/${token}/notice-to-vacate`, form);
      setDone(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't submit")); }
    finally { setBusy(false); }
  };

  if (done) return (
    <Shell title="📅 Notice Submitted" info={info} onBack={onBack}>
      <div className="p-6 bg-white border-2 border-emerald-600 text-center" data-testid="notice-done">
        <div className="text-4xl">✓</div>
        <div className="font-display font-black text-2xl mt-2 uppercase">Notice Received</div>
        <div className="text-sm mt-2">Expected move-out: <b>{done.expected_vacate_date}</b></div>
        <div className="text-xs text-slate-600 mt-1">{info?.crew_name} has been notified and will be in touch about move-out inspection.</div>
      </div>
    </Shell>
  );

  return (
    <Shell title="📅 Notice to Vacate" info={info} onBack={onBack}>
      <div className="p-4 bg-white border-2 border-[#0f172a] space-y-3">
        <input value={form.tenant_name} onChange={e => setForm({...form, tenant_name: e.target.value})} placeholder="Your full name" data-testid="notice-name" className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
        <input value={form.tenant_phone} onChange={e => setForm({...form, tenant_phone: e.target.value})} placeholder="Phone (optional)" data-testid="notice-phone" className="w-full h-11 px-3 border-2 border-slate-300 text-sm"/>
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Notice Length</div>
          <div className="grid grid-cols-3 gap-2" data-testid="notice-days-picker">
            {[30, 60, 90].map(d => (
              <button key={d} onClick={() => setForm({...form, notice_days: d})} data-testid={`notice-days-${d}`}
                className={`h-11 border-2 font-black uppercase text-xs tracking-widest ${form.notice_days === d ? "bg-fuchsia-600 text-white border-fuchsia-700" : "bg-white border-slate-300"}`}>
                {d} Days
              </button>
            ))}
          </div>
        </div>
        <textarea rows={3} value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} placeholder="Reason for moving (optional)" data-testid="notice-reason" className="w-full px-3 py-2 border-2 border-slate-300 text-sm resize-none"/>
        <button onClick={submit} disabled={busy} data-testid="notice-submit" className="w-full h-12 bg-red-600 text-white font-black uppercase text-xs tracking-widest disabled:opacity-40">
          {busy ? "Submitting…" : `Submit ${form.notice_days}-Day Notice`}
        </button>
      </div>
    </Shell>
  );
}
